import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

const SCRAMBLE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%*';

class WanedcMarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  async connectedCallback() {
    super.connectedCallback();

    // Init generation token: a superseded async init (disconnect/reconnect
    // race) must abort instead of stacking clones and intros on this node.
    this.#initSeq = (this.#initSeq || 0) + 1;

    // Restore liquid-pristine DOM so every connect starts clean. No-op first run.
    this.#restoreInitialDOM();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    // Hold the CSS loop until first viewport entry; the banner stays hidden
    // (opacity 0 via CSS) so below-fold marquees play their intro when seen.
    // 测量/克隆/设速也一并延后到首次可见（见 #initialize），避免页面上所有
    // marquee 在载入期就做强制布局与大量克隆。
    this.setAttribute('data-disabled', '');

    this.#boundResizeListener = this.#handleResize.bind(this);
    window.addEventListener('resize', this.#boundResizeListener);
    this.#setupObservers();
    // Re-check after fonts/images may have loaded
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.#scheduleRecalc());
    }
    window.addEventListener('load', () => this.#scheduleRecalc(), { once: true });

    this.#lastDataState = this.#readDataState();
    this.#syncHoverListeners(this.getAttribute('data-hover-behavior') || 'slow');

    this.#gateEntranceOnVisible();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Invalidate any in-flight async init so it aborts on resolve.
    this.#initSeq = (this.#initSeq || 0) + 1;
    // Drop pending debounced recalcs/flushes so they never mutate across reconnects.
    this.#pendingIntroResize = false;
    this.#scheduleRecalc.cancel?.();
    this.#handleResize.cancel?.();
    if (this.#boundResizeListener) {
      window.removeEventListener('resize', this.#boundResizeListener);
    } else {
      window.removeEventListener('resize', this.#handleResize);
    }
    if (this.#viewportObserver) {
      try {
        this.#viewportObserver.disconnect();
      } catch {}
      this.#viewportObserver = null;
    }
    this.#teardownObservers();
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
    this.#hoverListenersBound = false;
    this.#cancelIntro();
  }

  updatedCallback() {
    super.updatedCallback();
    if (!this.refs.marqueeItems || this.refs.marqueeItems.length === 0) return;
    this.#scheduleRecalc(true);
  }

  #animation = null;
  #marqueeWidth = null;
  #contentWidth = null;
  #isIntroPlaying = false;
  #introAnimation = null;
  #overlayData = [];
  #lastScrambleTime = 0;
  #scrambleFrame = null; // 私有字段：字符点亮 rAF 循环句柄，取消/交接需 cancel
  #introStartTransform = '';
  #resizeObserver = null;
  #viewportObserver = null;
  #initSeq = 0;
  #mutationObserver = null;
  #attributeObserver = null;
  #pendingIntroResize = false;
  #boundResizeListener = null;
  #lastDataState = null;
  #hoverListenersBound = false;
  #initialized = false;

  #slowDown = debounce(() => {
    if (this.#isIntroPlaying) return;
    if (this.#animation) return;
    const animation = this.refs.wrapper.getAnimations()[0];
    if (!animation) return;
    const hover = this.getAttribute('data-hover-behavior') || 'slow';
    let target = 0;
    if (hover === 'slow') target = 0.2;
    else if (hover === 'pause') target = 0;
    else return;
    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: target,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    if (this.#isIntroPlaying) {
      this.#slowDown.cancel();
      return;
    }
    this.#slowDown.cancel();
    const animation = this.refs.wrapper.getAnimations()[0];
    if (!animation || animation.playbackRate === 1) return;
    const from = this.#animation?.current ?? animation.playbackRate;
    this.#animation?.cancel();
    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;
    return content !== lastChild ? lastChild : null;
  }

  #setSpeed(value) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  async #queryNumberOfCopies() {
    const { marqueeItems } = this.refs;
    const wrapper = this.refs.wrapper; // 取 wrapper 引用
    return new Promise((resolve) => {
      if (!marqueeItems[0]) {
        return setTimeout(() => resolve({ numberOfCopies: 1, isHorizontalResize: true, contentWidthChanged: true }), 0);
      }
      // Get gap from computed style (shared var --marquee-gap)
      const getGap = () => {
        const cs = getComputedStyle(this);
        let g = parseFloat(cs.getPropertyValue('--marquee-gap'));
        if (Number.isNaN(g) || g === 0) {
          const cs2 = wrapper ? getComputedStyle(wrapper) : cs;
          g = parseFloat(cs2.getPropertyValue('--marquee-gap'));
        }
        if (Number.isNaN(g)) g = 24;
        return g;
      };
      const gap = getGap();
      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          const firstEntry = entries[0];
          if (!firstEntry) return;
          intersectionObserver.disconnect();
          const { width: marqueeWidth } = firstEntry.rootBounds ?? { width: 0 };
          const { width: marqueeItemsWidth } = firstEntry.boundingClientRect;
          const actualMarqueeWidth = marqueeWidth || this.getBoundingClientRect().width || 0;
          const contentW = this.refs.content ? this.refs.content.getBoundingClientRect().width : 0;
          const isHorizontalResize = Math.abs((this.#marqueeWidth || 0) - actualMarqueeWidth) > 1;
          const isContentResize = Math.abs((this.#contentWidth || 0) - contentW) > 1;
          this.#marqueeWidth = actualMarqueeWidth;
          this.#contentWidth = contentW;
          setTimeout(() => {
            let copies;
            if (marqueeItemsWidth === 0) {
              copies = 1;
            } else {
              // contentW = k*itemW + (k-1)*gap  =>  k >= (marqueeWidth+gap)/(itemW+gap)
              const needed = Math.ceil((actualMarqueeWidth + gap) / (marqueeItemsWidth + gap));
              copies = Math.max(1, needed + 1); // +1 buffer for subpixel and to avoid seam
            }
            resolve({
              numberOfCopies: copies,
              isHorizontalResize,
              contentWidthChanged: isContentResize,
            });
          }, 0);
        },
        { root: this }
      );
      intersectionObserver.observe(marqueeItems[0]);
    });
  }

  #calculateSpeed(numberOfCopies) {
    const secPerLoop = Number(this.getAttribute('data-speed-sec')) || 15;
    const base = secPerLoop * Math.sqrt(numberOfCopies);
    return base;
  }

  // 首次进入视口后才做测量/克隆/设速，避免载入期为所有实例同步付出代价。
  async #initialize() {
    if (this.#initialized) return;
    const { marqueeItems } = this.refs;
    if (!marqueeItems || marqueeItems.length === 0) return;

    const seq = this.#initSeq;
    const { numberOfCopies } = await this.#queryNumberOfCopies();
    if (seq !== this.#initSeq || !this.isConnected || this.#initialized) return;

    this.#addRepeatedItems(numberOfCopies);
    this.#duplicateContent();
    this.#setSpeed(this.#calculateSpeed(numberOfCopies));
    this.#initialized = true;
  }

  #scheduleRecalc = debounce(async (force = false) => {
    if (!this.isConnected) return;
    if (!this.#initialized) return;
    if (this.#isIntroPlaying) {
      this.#pendingIntroResize = true;
      return;
    }
    const { numberOfCopies: newNumberOfCopies, isHorizontalResize, contentWidthChanged } = await this.#queryNumberOfCopies();
    const content = this.refs.content;
    const currentNumberOfCopies = content ? content.children.length : 0;
    const copiesChanged = newNumberOfCopies !== currentNumberOfCopies;
    const cloneMissing = !this.clonedContent;
    // Recalc if viewport/content changed, copy count changed, clone was removed
    // (e.g. by an editor re-render) or the update was forced (settings changed).
    if (!force && !isHorizontalResize && !contentWidthChanged && !copiesChanged && !cloneMissing) return;
    const speed = this.#calculateSpeed(newNumberOfCopies);
    if (copiesChanged) {
      if (newNumberOfCopies > currentNumberOfCopies) {
        this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
      } else if (newNumberOfCopies < currentNumberOfCopies) {
        this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
      }
    }
    if (copiesChanged || cloneMissing) {
      this.#duplicateContent();
    }
    this.#setSpeed(speed);
    // Only restart the animation when the scrollable width actually changed or
    // the update was forced (editor speed/direction change) — avoids the
    // continuous-resize jump caused by always re-cloning and resetting.
    if (copiesChanged || force) {
      this.#restartAnimation();
    }
  }, 100);

  #handleResize = debounce(async () => {
    await this.#scheduleRecalc();
  }, 100);

  #restartAnimation({ sync = false } = {}) {
    const animations = this.refs.wrapper.getAnimations();
    if (sync) {
      for (const animation of animations) {
        // For reverse direction, currentTime 0 is at 'to' state (-50%), so align to visual 0
        const isReverse = this.getAttribute('data-movement-direction') === 'reverse';
        if (isReverse) {
          const timing = animation.effect ? animation.effect.getTiming() : null;
          const dur = timing && timing.duration ? Number(timing.duration) : 0;
          // duration may be string like "15000" or number; ensure numeric
          animation.currentTime = Number.isFinite(dur) && dur > 0 ? dur : 0;
          // Fallback: if duration not available, use 0 (will be corrected on next frame)
        } else {
          animation.currentTime = 0;
        }
      }
    } else {
      requestAnimationFrame(() => {
        for (const animation of animations) {
          const isReverse = this.getAttribute('data-movement-direction') === 'reverse';
          if (isReverse) {
            const timing = animation.effect ? animation.effect.getTiming() : null;
            const dur = timing && timing.duration ? Number(timing.duration) : 0;
            animation.currentTime = Number.isFinite(dur) && dur > 0 ? dur : 0;
          } else {
            animation.currentTime = 0;
          }
        }
      });
    }
  }

  #duplicateContent() {
    this.clonedContent?.remove();
    const clone = this.refs.content.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');
    // Fix double-counting: cloned subtree still has ref="marqueeItems[]"
    clone.querySelectorAll('[ref]').forEach((el) => el.removeAttribute('ref'));
    this.refs.wrapper.appendChild(clone);
    // Force Component to update refs after DOM change (if available)
    if (this._updateRefs) this._updateRefs();
    else if (this._refs) this._refs = null;
  }

  #addRepeatedItems(numberOfCopies) {
    const { content, marqueeItems } = this.refs;
    if (!marqueeItems[0]) return;
    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = marqueeItems[0].cloneNode(true);
      content.appendChild(clone);
    }
  }

  #removeRepeatedItems(numberOfCopies) {
    const { content } = this.refs;
    const children = Array.from(content.children);
    const itemsToRemove = Math.min(numberOfCopies, children.length - 1);
    for (let i = 0; i < itemsToRemove; i++) {
      content.lastElementChild?.remove();
    }
  }

  #setupObservers() {
    const content = this.refs.content;
    const wrapper = this.refs.wrapper;
    if (content && typeof ResizeObserver !== 'undefined') {
      this.#resizeObserver = new ResizeObserver(() => this.#scheduleRecalc());
      this.#resizeObserver.observe(content);
      if (wrapper) this.#resizeObserver.observe(wrapper);
    }
    if (content && typeof MutationObserver !== 'undefined') {
      this.#mutationObserver = new MutationObserver(() => this.#scheduleRecalc());
      this.#mutationObserver.observe(content, { childList: true, subtree: true, characterData: true });
    }
    // Watch the component's own data attributes + inline style so theme-editor
    // re-renders (which morph's copyAttributes applies directly to this live
    // element) re-sync speed/direction and restore the JS-set --marquee-speed.
    this.#attributeObserver = new MutationObserver(() => this.#handleAttributeChange());
    this.#attributeObserver.observe(this, {
      attributes: true,
      attributeFilter: ['data-speed-sec', 'data-movement-direction', 'data-hover-behavior', 'style'],
    });
  }

  #teardownObservers() {
    if (this.#resizeObserver) {
      try {
        this.#resizeObserver.disconnect();
      } catch {}
      this.#resizeObserver = null;
    }
    if (this.#mutationObserver) {
      try {
        this.#mutationObserver.disconnect();
      } catch {}
      this.#mutationObserver = null;
    }
    if (this.#attributeObserver) {
      try {
        this.#attributeObserver.disconnect();
      } catch {}
      this.#attributeObserver = null;
    }
  }

  #readDataState() {
    return {
      speed: this.getAttribute('data-speed-sec'),
      hover: this.getAttribute('data-hover-behavior'),
      direction: this.getAttribute('data-movement-direction'),
    };
  }

  #syncHoverListeners(hover) {
    const shouldBind = hover !== 'normal';
    if (shouldBind && !this.#hoverListenersBound) {
      this.addEventListener('pointerenter', this.#slowDown);
      this.addEventListener('pointerleave', this.#speedUp);
      this.#hoverListenersBound = true;
    } else if (!shouldBind && this.#hoverListenersBound) {
      this.removeEventListener('pointerenter', this.#slowDown);
      this.removeEventListener('pointerleave', this.#speedUp);
      this.#hoverListenersBound = false;
    }
  }

  #handleAttributeChange() {
    const next = this.#readDataState();
    const previous = this.#lastDataState;
    this.#lastDataState = next;
    const dataChanged =
      !previous || previous.speed !== next.speed || previous.direction !== next.direction || previous.hover !== next.hover;
    // morph's copyAttributes overwrites the whole inline style, dropping the
    // JS-set --marquee-speed, so reapply whenever it goes missing.
    const speedVarMissing = this.style && !this.style.getPropertyValue('--marquee-speed');
    if (!dataChanged && !speedVarMissing) return;
    this.#syncHoverListeners(next.hover);
    this.#scheduleRecalc(true);
  }

  // Tear down any previous init's DOM (extra copies, clone, overlays) so every
  // connect starts from the liquid-pristine single repeated-items. No-op first run.
  #restoreInitialDOM() {
    if (this.#overlayData.length > 0) {
      try {
        this.#removeOverlays();
      } catch {}
    } else {
      this.querySelectorAll('.wanedc-scramble-overlay').forEach((el) => el.remove());
      this.querySelectorAll('.wanedc-announcement__text').forEach((el) => {
        if (el.style.visibility === 'hidden') el.style.visibility = '';
      });
    }
    const wrapper = this.querySelector('.wanedc-marquee__wrapper');
    const content = this.querySelector('.wanedc-marquee__content');
    if (wrapper && content) {
      [...wrapper.children].forEach((child) => {
        if (child !== content) child.remove();
      });
      const keep =
        content.querySelector(':scope > .wanedc-marquee__repeated-items') || content.firstElementChild;
      [...content.children].forEach((child) => {
        if (child !== keep) child.remove();
      });
    }
    this.removeAttribute('data-loaded');
    this.#initialized = false;
  }

  // Defer reveal + intro until the component first enters the viewport so the
  // entrance plays when actually seen. Already-visible instances fire at once.
  #gateEntranceOnVisible() {
    const reveal = async () => {
      await this.#initialize();
      if (!this.isConnected) return;
      this.setAttribute('data-loaded', '');
      this.#startIntroIfEnabled();
      if (!this.#isIntroPlaying) this.removeAttribute('data-disabled');
    };
    if (!window.IntersectionObserver || (window.Shopify && window.Shopify.designMode)) {
      reveal();
      return;
    }
    this.#viewportObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        try {
          this.#viewportObserver.disconnect();
        } catch {}
        this.#viewportObserver = null;
        reveal();
      },
      { root: null, threshold: 0.01, rootMargin: '0px 0px -2% 0px' }
    );
    this.#viewportObserver.observe(this);
  }

  #startIntroIfEnabled() {
    if (this.#isIntroPlaying) return;
    const effect = this.getAttribute('data-intro-effect') || 'scramble';
    if (effect === 'none') return;
    if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const baseDuration = Number(this.getAttribute('data-intro-duration')) || 1500;
    const wrapper = this.refs.wrapper;
    if (!wrapper) return;

    // Disable CSS infinite animation during intro, use manual transform for side entry
    const direction = this.getAttribute('data-movement-direction') || this.getAttribute('data-speed-sec') ? this.style.getPropertyValue('--marquee-direction') : 'normal';
    // Fallback to attribute directly
    const dirAttr = this.getAttribute('data-movement-direction') || 'normal';
    const isReverse = dirAttr === 'reverse';
    const startOffset = isReverse ? '-40vw' : '40vw';

    // 定速模式：进场速度与距离解耦，时长按实际滑动距离等比缩放，保证两方向视觉速度一致
    const vw = window.innerWidth || document.documentElement.clientWidth || 1280;
    const actualDistancePx = vw;
    const duration = Math.max(200, Math.round((baseDuration * actualDistancePx) / Math.max(1, vw)));
    const revealDuration = Math.max(1, duration - 500); // 点亮在进场完成前 0.5s 全部点亮，留 0.5s 给最后字符渐显收尾

    this.#isIntroPlaying = true;
    this.setAttribute('data-disabled', '');
    this.#introStartTransform = wrapper.style.transform || '';
    wrapper.style.transform = `translate3d(${startOffset}, 0, 0)`;
    // Force reflow so transform is applied before animation
    void wrapper.offsetWidth; // 强制回流，确保 CSS 动画样式切回前生效

    this.#overlayData = this.#createOverlays();
    this.#introAnimation = wrapper.animate( // WAAPI 合成器驱动位移，不占主线程
      [
        { transform: `translate3d(${startOffset}, 0, 0)` }, // 动画起点：完全离屏的侧滑位置
        { transform: 'translate3d(0, 0, 0)' }, // 动画终点：进场完成位置
      ],
      { duration, easing: 'cubic-bezier(0, 0, 0.4, 1)', fill: 'forwards' } // 近似 smoothstep 缓动；fill:forwards 保持终点防闪回
    );

    if (this.#overlayData.length === 0) {
      this.#lastScrambleTime = 0;
      this.#introAnimation.finished.then(() => this.#handoffIntro(isReverse)).catch(() => {}); // WAAPI 结束后统一交接给 CSS 循环；catch 防 cancel 引发的 AbortError
      return;
    }

    this.#scrambleOverlays(0); // 首帧：初始全部字符为 opacity:0
    this.#lastScrambleTime = 0;
    const scrambleLoop = () => { // 独立 rAF：只读 WAAPI 进度驱动字符点亮，位移已在合成器
      if (!this.#isIntroPlaying) return;
      const t = this.#introAnimation?.currentTime ?? 0; // 读 WAAPI 当前时间(ms)
      const revealProgress = Math.min(Number(t) / revealDuration, 1); // 点亮进度：提前 0.5s 到 1，最后字符有充足渐显时间
      const progress = duration > 0 ? Math.min(Number(t) / duration, 1) : 1; // 真实进场进度，用于续帧/停止
      const now = performance.now();
      if (now - this.#lastScrambleTime > 50 || revealProgress >= 1) {
        this.#scrambleOverlays(revealProgress);
        this.#lastScrambleTime = now;
      }
      if (progress < 1) {
        this.#scrambleFrame = requestAnimationFrame(scrambleLoop); // 启动/续排点亮循环，保存句柄供取消
      } else {
        this.#scrambleFrame = null; // 清空点亮 rAF 句柄
      }
    };
    this.#scrambleFrame = requestAnimationFrame(scrambleLoop);
    this.#introAnimation.finished.then(() => this.#handoffIntro(isReverse)).catch(() => {});
  }

  #handoffIntro(isReverse) { // 进场结束的统一交接：位移从 WAAPI 无缝切回 CSS 循环
    const wrapper = this.refs.wrapper;
    if (!wrapper) { // wrapper 丢失（组件被移除）时兜底复位状态
      this.#isIntroPlaying = false;
      this.#introAnimation = null;
      this.#scrambleFrame = null;
      return;
    }
    if (this.#scrambleFrame) { // 若点亮循环仍在运行则停止 // 若点亮 rAF 仍在运行则一并取消
      cancelAnimationFrame(this.#scrambleFrame); // 取消 rAF // 取消点亮循环
      this.#scrambleFrame = null;
    }
    this.#removeOverlays(); // 移除字符覆盖层并恢复原文可见性，避免交接瞬间宽度突变
    this.#introAnimation?.cancel(); // 取消（已结束）WAAPI，释放 fill:forwards // 取消进行中的 WAAPI 进场动画
    this.removeAttribute('data-disabled');
    void wrapper.offsetWidth;
    const anims = wrapper.getAnimations(); // 取 wrapper 当前动画（此时仅剩 CSS 循环）
    for (const a of anims) { // 逐个对齐动画时间
      const timing = a.effect ? a.effect.getTiming() : null;
      const dur = timing && timing.duration ? Number(timing.duration) : 0;
      a.currentTime = isReverse && Number.isFinite(dur) && dur > 0 ? dur : 0; // 反向对齐到时长末尾、正向对齐到 0，防跳帧
    }
    wrapper.style.transform = ''; // 清空行内 transform，交还给 CSS 动画接管
    this.#isIntroPlaying = false;
    this.#introAnimation = null;
    if (this.#pendingIntroResize) {
      this.#pendingIntroResize = false;
      setTimeout(() => this.#scheduleRecalc(), 300); // 延迟 300ms 补重算，避开交接瞬间
    }
  }

  #cancelIntro() { // 取消进场动画并恢复状态（disconnected 时调用）
    this.#introAnimation?.cancel();
    if (this.#scrambleFrame) {
      cancelAnimationFrame(this.#scrambleFrame);
      this.#scrambleFrame = null;
    }
    this.#introAnimation = null;
    const wrapper = this.refs.wrapper;
    if (wrapper) {
      wrapper.style.transform = this.#introStartTransform || '';
    }
    this.removeAttribute('data-disabled');
    if (this.#overlayData.length > 0) {
      this.#removeOverlays();
    }
    this.#isIntroPlaying = false;
    this.#lastScrambleTime = 0;
    if (this.#pendingIntroResize) {
      this.#pendingIntroResize = false;
      setTimeout(() => this.#scheduleRecalc(), 0);
    }
  }

  #createOverlays() { // 创建逐字符随机点亮-渐显覆盖层：原文隐藏占位，字符 span 随机点亮
    const items = Array.from(this.querySelectorAll('.wanedc-announcement__item'));
    const data = [];
    for (const item of items) {
      const textEl = item.querySelector('.wanedc-announcement__text');
      if (!textEl) continue;
      const original = textEl.textContent ?? '';
      if (!original.trim()) continue;
      const trimmed = original.trim(); // 去掉首尾换行/空白（Liquid 模板缩进残留）
      const prevVisibility = textEl.style.visibility;
      textEl.style.visibility = 'hidden'; // 隐藏原文但保留布局尺寸，锁定占位宽度
      const chars = Array.from(trimmed); // 按码点切分字符数组（兼容中文/emoji），已去首尾空白
      const overlay = document.createElement('span');
      overlay.className = textEl.className;
      overlay.classList.add('wanedc-scramble-overlay');
      const origStyle = textEl.getAttribute('style') || '';
      if (origStyle) overlay.setAttribute('style', origStyle);
      overlay.style.position = 'absolute'; // 绝对定位：脱离文档流，不撑宽容器
      overlay.style.inset = '0';
      overlay.style.display = 'flex';
      overlay.style.alignItems = 'center';
      overlay.style.justifyContent = 'center';
      overlay.style.whiteSpace = 'nowrap';
      overlay.style.pointerEvents = 'none';
      overlay.style.visibility = 'visible';
      const spans = chars.map((ch) => { // 逐字符重建为独立 span，尊重每字符真实宽度，字形不变宽度恒定
        const s = document.createElement('span');
        s.textContent = ch === ' ' ? '\u00A0' : ch; // 词间空格用不换行空格，避免 flex 折叠为零宽
        s.style.opacity = '0'; // 字符初态不可见
        s.style.transition = 'opacity 0.45s ease'; // 柔和渐显
        overlay.appendChild(s);
        return s;
      });
      const revealOrder = chars.map((_, idx) => idx); // 随机揭示顺序（Fisher-Yates 洗牌），避免按顺序逐字显现
      for (let i = revealOrder.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [revealOrder[i], revealOrder[j]] = [revealOrder[j], revealOrder[i]];
      }
      item.appendChild(overlay);
      data.push({ item, textEl, overlay, original, prevVisibility, chars, spans, revealOrder, shownCount: 0 }); // 缓存字符/span/随机顺序与已点亮计数
    }
    return data;
  }

  #scrambleOverlays(progress) { // 按进度随机点亮字符：已点亮的不回退，仅翻转 opacity，宽度恒定
    const p = Math.min(Math.max(progress, 0), 1);
    for (const entry of this.#overlayData) {
      const { spans, revealOrder, chars } = entry;
      const target = Math.round(p * chars.length); // 按进度计算应点亮的字符数
      if (target === entry.shownCount) continue; // 点亮数未变化则跳过，避免无意义 DOM 写
      for (let k = entry.shownCount; k < target; k++) {
        const idx = revealOrder[k]; // 按随机顺序逐一点亮
        spans[idx].style.opacity = '1'; // 点亮对应字符 span
      }
      entry.shownCount = target; // 记录已点亮计数（单调不回退）
    }
  }

  #removeOverlays() {
    for (const entry of this.#overlayData) {
      const { textEl, overlay, prevVisibility } = entry;
      textEl.style.visibility = prevVisibility;
      overlay.remove();
    }
    this.#overlayData = [];
  }
}

function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;
  function animate(currentTime) {
    if (cancelled) return;
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;
    onUpdate(currentValue);
    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }
  requestAnimationFrame(animate);
  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('wanedc-marquee-component')) {
  customElements.define('wanedc-marquee-component', WanedcMarqueeComponent);
}
